import { Dropdown } from '@carbon/react';
import { DateRangeFilter, useReferenceOptions } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import type { ReactNode } from 'react';
import type { ReportRange } from './reportRange';
import './Reports.scss';

interface Option {
  id: string;
  label: string;
}

/**
 * The filters above an HO report: the store (or all stores) and the date range, applied as soon as they change (a range once
 * both ends are set). A report adds its own, e.g. what to compare with.
 */
export function ReportFilters({
  range,
  onChange,
  children,
}: {
  range: ReportRange;
  onChange(range: ReportRange): void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const stores = useReferenceOptions('org.store');
  const items: Option[] = [
    { id: '', label: t('reports.allStores') },
    ...(stores.data ?? []).map((s) => ({ id: s.value, label: s.label })),
  ];
  return (
    <div className="pos-report-filters">
      <div className="pos-report-filters__field">
        <Dropdown<Option>
          id="report-store"
          titleText={t('reports.store')}
          label={t('reports.store')}
          items={items}
          itemToString={(o) => o?.label ?? ''}
          selectedItem={items.find((o) => o.id === range.storeId) ?? items[0]}
          onChange={({ selectedItem }) => onChange({ ...range, storeId: selectedItem?.id ?? '' })}
        />
      </div>
      <DateRangeFilter
        id="report-dates"
        legendText={t('reports.period')}
        inline
        fromLabel={t('reports.periodFrom')}
        toLabel={t('reports.periodTo')}
        from={range.from}
        to={range.to}
        onChange={({ from, to }) => {
          if (from && to) onChange({ ...range, from, to });
        }}
      />
      {children}
    </div>
  );
}
